import React, { useState, useRef } from 'react'
import { Pie as PieChart } from 'react-chartjs-2'
import { Chart as ChartJS, RadialLinearScale, ArcElement, Tooltip, Legend, Plugin, ChartOptions } from 'chart.js'
import colors from '../colors'
import { useSelector } from 'react-redux'
import props from '../../../../redux/props'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import Button from '../../Button/Button'
import { formatNumber } from '../../../../utility'

ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend)

// Note: We do NOT register ChartDataLabels globally.
// We'll pass it selectively via the `plugins` prop in <PieChart />

export type Item = {
  label: string
  value: number
  borderColor?: string
  backgroundColor?: string
}

type Props = {
  items: Item[]
  enableDownload?: boolean
  showLegend?: boolean
}

const Pie: React.FC<Props> = ({ items, enableDownload = false, showLegend = false }) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  // For Chart.js v4 + react-chartjs-2 v5, the ref will point to a Chart.js instance
  const chartRef = useRef<any>(null)
  const [image, setImage] = useState<string | null>(null)

  const data = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        data: items.map((i) => i.value),
        backgroundColor: items.map((item, i) => item.backgroundColor || colors[i].backgroundColor),
        borderColor: items.map((item, i) => item.borderColor || colors[i].borderColor),
        borderWidth: 1,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: showLegend
        ? {
            top: 20,
            bottom: 0,
            left: 60,
            right: 60,
          }
        : 60,
      autoPadding: true,
    },
    plugins: {
      legend: showLegend
        ? {
            display: true,
            position: 'bottom',
            align: 'start',
            labels: {
              fullSize: false,
              usePointStyle: false,
              pointStyle: 'rectRounded',
              padding: 10,
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12,
              },
            },
          }
        : { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || ''
            if (context.parsed) {
              const value = context.parsed
              return `${formatNumber(value)}${t.generic.percent}`
            }
            return label
          },
        },
      },
      datalabels: {
        color: '#000',
        anchor: 'end' as const,
        align: 'end' as const,
        formatter: (value) => `${formatNumber(value)}${t.generic.percent}`,
      },
    },
    // Whenever the chart finishes drawing/animating, capture its image.
    animation: {
      onComplete: () => {
        if (chartRef.current && enableDownload) {
          // chartRef.current is the Chart.js instance
          const chart = chartRef.current
          // Access the canvas from the chart instance
          const canvas = chart.canvas
          setImage(canvas.toDataURL())
        }
      },
    },
  } as ChartOptions<'pie'>

  const download = () => {
    if (!image) return
    const a = document.createElement('a')
    a.href = image
    a.download = 'chart.png'
    a.click()
  }

  return (
    <div className="w-full aspect-square">
      {image && enableDownload && <Button text={t.generic.download} onClick={download} small className="ml-auto mt-5" />}
      <PieChart ref={chartRef} data={data} options={options} plugins={enableDownload && [ChartDataLabels as Plugin<'pie'>]} />
    </div>
  )
}

export default Pie
